import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  AdminPanelBusinessMarginalityFilterRequestDto,
  AdminPanelCarriersPortfolioFilterRequestDto,
  AdminPanelCashFlowFilterRequestDto,
  AdminPanelClientsPortfolioFilterRequestDto,
  AdminPanelDealStatusFilter,
  AdminPanelEmployeeEfficiencyFilterRequestDto,
  AdminPanelNewCarriersFilterRequestDto,
  AdminPanelOverdueDebtorsFilterRequestDto,
} from "../../../api/apiServices/backOffice/controllers/adminPanel/adminPanel.requestsDto";
import AdminPanelBusinessMarginalityResponseDto from "../../../dto/AdminPanel/AdminPanelBusinessMarginalityResponseDto";
import AdminPanelCarriersPortfolioResponseDto from "../../../dto/AdminPanel/AdminPanelCarriersPortfolioResponseDto";
import AdminPanelCashFlowResponseDto from "../../../dto/AdminPanel/AdminPanelCashFlowResponseDto";
import AdminPanelClientsPortfolioResponseDto from "../../../dto/AdminPanel/AdminPanelClientsPortfolioResponseDto";
import AdminPanelEmployeeEfficiencyResponseDto from "../../../dto/AdminPanel/AdminPanelEmployeeEfficiencyResponseDto";
import AdminPanelNewCarriersResponseDto from "../../../dto/AdminPanel/AdminPanelNewCarriersResponseDto";
import AdminPanelOverdueDebtorsResponseDto from "../../../dto/AdminPanel/AdminPanelOverdueDebtorsResponseDto";

const initialState = {
  dealStatus: "ALL" as AdminPanelDealStatusFilter,
  overdueDebtorsDealStatus: "ALL" as AdminPanelDealStatusFilter,
  cashFlowLoading: true,
  cashFlowError: false,
  cashFlow: undefined as AdminPanelCashFlowResponseDto | undefined,
  overdueDebtorsLoading: true,
  overdueDebtorsError: false,
  overdueDebtors: undefined as AdminPanelOverdueDebtorsResponseDto | undefined,
  businessMarginalityLoading: true,
  businessMarginalityError: false,
  businessMarginality: undefined as AdminPanelBusinessMarginalityResponseDto | undefined,
  clientsPortfolioLoading: true,
  clientsPortfolioError: false,
  clientsPortfolio: undefined as AdminPanelClientsPortfolioResponseDto | undefined,
  carriersPortfolioLoading: true,
  carriersPortfolioError: false,
  carriersPortfolio: undefined as AdminPanelCarriersPortfolioResponseDto | undefined,
  newCarriersLoading: true,
  newCarriersError: false,
  newCarriers: undefined as AdminPanelNewCarriersResponseDto | undefined,
  employeeEfficiencyLoading: true,
  employeeEfficiencyError: false,
  employeeEfficiency: undefined as AdminPanelEmployeeEfficiencyResponseDto | undefined,
};

const reportsSlice = createSlice({
  name: "adminPanelReports",
  initialState,
  reducers: {
    changeDealStatus(state, action: PayloadAction<AdminPanelDealStatusFilter>) {
      state.dealStatus = action.payload;
    },
    changeOverdueDebtorsDealStatus(state, action: PayloadAction<AdminPanelDealStatusFilter>) {
      state.overdueDebtorsDealStatus = action.payload;
    },
    loadCashFlow(state, _action: PayloadAction<AdminPanelCashFlowFilterRequestDto>) {
      state.cashFlowLoading = true;
      state.cashFlowError = false;
    },
    loadCashFlowSuccess(state, action: PayloadAction<AdminPanelCashFlowResponseDto>) {
      state.cashFlowLoading = false;
      state.cashFlow = action.payload;
    },
    loadCashFlowFailure(state) {
      state.cashFlowLoading = false;
      state.cashFlowError = true;
    },
    loadOverdueDebtors(state, _action: PayloadAction<AdminPanelOverdueDebtorsFilterRequestDto>) {
      state.overdueDebtorsLoading = true;
      state.overdueDebtorsError = false;
    },
    loadOverdueDebtorsSuccess(state, action: PayloadAction<AdminPanelOverdueDebtorsResponseDto>) {
      state.overdueDebtorsLoading = false;
      state.overdueDebtors = action.payload;
    },
    loadOverdueDebtorsFailure(state) {
      state.overdueDebtorsLoading = false;
      state.overdueDebtorsError = true;
    },
    loadBusinessMarginality(state, _action: PayloadAction<AdminPanelBusinessMarginalityFilterRequestDto>) {
      state.businessMarginalityLoading = true;
      state.businessMarginalityError = false;
    },
    loadBusinessMarginalitySuccess(state, action: PayloadAction<AdminPanelBusinessMarginalityResponseDto>) {
      state.businessMarginalityLoading = false;
      state.businessMarginality = action.payload;
    },
    loadBusinessMarginalityFailure(state) {
      state.businessMarginalityLoading = false;
      state.businessMarginalityError = true;
    },
    loadClientsPortfolio(state, _action: PayloadAction<AdminPanelClientsPortfolioFilterRequestDto>) {
      state.clientsPortfolioLoading = true;
      state.clientsPortfolioError = false;
    },
    loadClientsPortfolioSuccess(state, action: PayloadAction<AdminPanelClientsPortfolioResponseDto>) {
      state.clientsPortfolioLoading = false;
      state.clientsPortfolio = action.payload;
    },
    loadClientsPortfolioFailure(state) {
      state.clientsPortfolioLoading = false;
      state.clientsPortfolioError = true;
    },
    loadCarriersPortfolio(state, _action: PayloadAction<AdminPanelCarriersPortfolioFilterRequestDto>) {
      state.carriersPortfolioLoading = true;
      state.carriersPortfolioError = false;
    },
    loadCarriersPortfolioSuccess(state, action: PayloadAction<AdminPanelCarriersPortfolioResponseDto>) {
      state.carriersPortfolioLoading = false;
      state.carriersPortfolio = action.payload;
    },
    loadCarriersPortfolioFailure(state) {
      state.carriersPortfolioLoading = false;
      state.carriersPortfolioError = true;
    },
    loadNewCarriers(state, _action: PayloadAction<AdminPanelNewCarriersFilterRequestDto>) {
      state.newCarriersLoading = true;
      state.newCarriersError = false;
    },
    loadNewCarriersSuccess(state, action: PayloadAction<AdminPanelNewCarriersResponseDto>) {
      state.newCarriersLoading = false;
      state.newCarriers = action.payload;
    },
    loadNewCarriersFailure(state) {
      state.newCarriersLoading = false;
      state.newCarriersError = true;
    },
    loadEmployeeEfficiency(state, _action: PayloadAction<AdminPanelEmployeeEfficiencyFilterRequestDto>) {
      state.employeeEfficiencyLoading = true;
      state.employeeEfficiencyError = false;
    },
    loadEmployeeEfficiencySuccess(state, action: PayloadAction<AdminPanelEmployeeEfficiencyResponseDto>) {
      state.employeeEfficiencyLoading = false;
      state.employeeEfficiency = action.payload;
    },
    loadEmployeeEfficiencyFailure(state) {
      state.employeeEfficiencyLoading = false;
      state.employeeEfficiencyError = true;
    },
  },
});

export default reportsSlice;
